/**
 * ProjectHeader Component - Jira-style project header with breadcrumb and actions
 */
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Globe,
  Calendar,
  List,
  Columns,
  FileText,
  Maximize2,
  Minimize2,
  Users,
  MoreHorizontal,
  CalendarDays,
  Edit,
  Trash2,
  X
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Share from '@/components/Share';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { deleteProject, fetchProjects, setCurrentProject, updateProject } from '@/store/projectSlice';
import { setProjectHeaderFullscreen } from '@/store/uiSlice';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';

interface ProjectHeaderProps {
  projectName?: string;
  projectIcon?: string;
  projectColor?: string;
  projectId?: string;
  projectDescription?: string;
}

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string }) => Promise<void>;
  initialName: string;
  initialDescription: string;
  isLoading: boolean;
}

const projectTabs = [
  { id: 'summary', label: 'Summary', icon: Globe, path: '/projects/:id/summary' },
  { id: 'timeline', label: 'Timeline', icon: Calendar, path: '/projects/:id/timeline' },
  { id: 'backlog', label: 'Backlog', icon: List, path: '/projects/:id/backlog' },
  { id: 'board', label: 'Board', icon: Columns, path: '/projects/:id/board' },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays, path: '/projects/:id/calendar' },
  { id: 'people', label: 'People', icon: Users, path: '/projects/:id/people' },
  { id: 'list', label: 'List', icon: FileText, path: '/projects/:id/list' },
];

// Edit Project Modal Component
const EditProjectModal = ({
  isOpen,
  onClose,
  onSave,
  initialName,
  initialDescription,
  isLoading
}: EditProjectModalProps) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
    }
  }, [isOpen, initialName, initialDescription]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node) && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isLoading, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 3 || isLoading) return;

    await onSave({
      name: name.trim(),
      description: description.trim()
    });
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-md"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit Project</h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Enter project name"
              required
              autoFocus
            />
            <div className="text-xs text-gray-500 mt-1">
              {name.length}/200 characters
            </div>
            {name.trim().length < 3 && (
              <div className="text-xs text-red-500 mt-1">
                Project name must be at least 3 characters
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLoading}
              rows={4}
              maxLength={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Enter project description"
            />
            <div className="text-xs text-gray-500 mt-1">
              {description.length}/1000 characters
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isLoading || name.trim().length < 3}
              className="btn btn-primary flex-1"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ProjectHeader = ({
  projectName = "Sample Project",
  projectIcon = "🐨",
  projectColor = "bg-blue-500",
  projectId = "",
  projectDescription = ""
}: ProjectHeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAdmin, user } = useAppSelector((state) => state.auth);
  const { currentProject } = useAppSelector((state) => state.projects);
  const isFullscreen =
    useAppSelector((state) => ((state as any).ui?.projectHeaderFullscreen as boolean) ?? false);
  const [showMoreTabs, setShowMoreTabs] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const moreTabsRef = useRef<HTMLDivElement | null>(null);

  // Determine current tab based on path
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.endsWith('/summary')) return 'summary';
    if (path.includes('/backlog')) return 'backlog';
    if (path.includes('/board')) return 'board';
    if (path.includes('/reports')) return 'reports';
    if (path.includes('/tasks')) return 'list';
    if (path.includes('/kanban')) return 'board';
    if (path.includes('/timeline')) return 'timeline';
    if (path.includes('/calendar')) return 'calendar';
    if (path.includes('/people')) return 'people';
    if (path.includes('/list')) return 'list';
    if (path.includes('/forms')) return 'forms';
    if (path.includes('/goals')) return 'goals';
    if (path.includes('/development')) return 'development';
    if (path.includes('/code')) return 'code';
    if (path.includes('/archived')) return 'archived';
    if (path.includes('/pages')) return 'pages';
    // If project root path, return summary
    if (path.match(/^\/projects\/[^/]+$/)) return 'summary';
    return 'summary';
  };

  const currentTab = getCurrentTab();
  const visibleTabs = projectTabs.slice(0, 8);
  const hiddenTabs = projectTabs.slice(8);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (moreTabsRef.current && !moreTabsRef.current.contains(event.target as Node)) {
        setShowMoreTabs(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dispatch(setProjectHeaderFullscreen(false));
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [dispatch, isFullscreen]);

  useEffect(() => {
    return () => {
      dispatch(setProjectHeaderFullscreen(false));
    };
  }, [dispatch]);

  const toggleFullscreen = () => {
    dispatch(setProjectHeaderFullscreen(!isFullscreen));
  };

  const handleOpenEditModal = () => {
    if (!isAdmin || !projectId) return;
    setIsMenuOpen(false);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    if (!isUpdating) {
      setIsEditModalOpen(false);
    }
  };

  const handleSaveProject = async (data: { name: string; description: string }) => {
    if (!projectId) return;

    setIsUpdating(true);
    try {
      await dispatch(updateProject({
        id: projectId,
        data: {
          name: data.name,
          description: data.description
        }
      })).unwrap();
      setIsEditModalOpen(false);
      dispatch(fetchProjects({ is_active: true, organization_id:user?.organization_id }));
    } catch (error) {
      // Error handled in thunk toast
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectId) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteProject(projectId)).unwrap();
      // Navigate immediately to prevent components from fetching deleted project
      navigate('/projects', { replace: true });
      // Clear current project and refresh list after navigation
      dispatch(setCurrentProject(null));
      dispatch(fetchProjects({ is_active: true,organization_id:user?.organization_id }));
    } catch (error) {
      // Error handled in thunk toast
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const wrapperClasses = `bg-white border-b border-gray-200 transition-all duration-200`;
  const infoPaddingClass = `${isFullscreen ? 'px-6 sm:px-8 lg:px-10 py-6' : 'px-4 py-3'} border-b border-gray-100`;
  const tabsPaddingClass = isFullscreen ? 'px-6 sm:px-8 lg:px-10 pt-4 pb-3' : 'px-4';
  const description = currentProject?.description || projectDescription;

  return (
    <div className={wrapperClasses}>
        {/* Project Info Bar */}
        <div className={infoPaddingClass}>
          <div className={`flex flex-col ${isFullscreen ? 'gap-6' : 'gap-4'}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 ${projectColor} rounded flex items-center justify-center text-white text-xs`}>
                    {projectIcon}
                  </div>
                  <span className="text-[20px] font-bold text-gray-900 leading-6">
                    {currentProject?.name || projectName}
                  </span>
                </div>
                <button
                  className="p-1 hover:bg-gray-100 rounded"
                  onClick={() => navigate(`/projects/${projectId}/people?add=1`)}
                  title="Manage people"
                >
                  <Users className="w-4 h-4 text-gray-500" />
                </button>
                {isAdmin && (
                  <div className="relative" ref={menuRef}>
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      onClick={() => setIsMenuOpen((prev) => !prev)}
                      title="Project options"
                      disabled={!projectId}
                    >
                      <MoreHorizontal className="w-4 h-4 text-gray-500" />
                    </button>

                    {isMenuOpen && projectId && (
                      <div className="absolute left-full ml-0 mt-0 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                        <button
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          onClick={handleOpenEditModal}
                        >
                          <Edit className="w-4 h-4" />
                          Edit project
                        </button>

                        <button
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete project
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Share label="Share" subject={`Shared view: ${projectName}`} className="btn btn-secondary flex items-center gap-2" projectId={projectId} />
                <button
                  className={`p-2 rounded transition ${isFullscreen ? 'bg-gray-100 text-gray-700' : 'hover:bg-gray-100'}`}
                  onClick={toggleFullscreen}
                  aria-pressed={isFullscreen}
                  aria-label={isFullscreen ? 'Restore header' : 'Maximize header'}
                  title={isFullscreen ? 'Restore header' : 'Maximize header'}
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-4 h-4 text-gray-600" />
                  ) : (
                    <Maximize2 className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              </div>
            </div>

            {isFullscreen && description && (
              <div className="text-sm text-gray-600 max-w-4xl leading-6">
                {description}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className={tabsPaddingClass}>
          <div className="flex items-center gap-1 overflow-x-auto">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = currentTab === tab.id;

              return (
                <Link
                  key={tab.id}
                  to={tab.path.replace(':id', projectId.toString())}
                  className={`group flex items-center gap-2 px-2.5 pt-1 pb-2 text-sm border-b-2 transition-colors whitespace-nowrap ${isActive
                      ? 'text-blue-600 border-blue-600 font-semibold'
                      : 'text-gray-700 border-transparent hover:text-gray-900 hover:border-gray-300 font-medium'
                    }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'}`} />
                  {tab.label}
                </Link>
              );
            })}

            {hiddenTabs.length > 0 && (
              <div className="relative" ref={moreTabsRef}>
                <button
                  onClick={() => setShowMoreTabs(!showMoreTabs)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 border-b-2 border-transparent hover:text-gray-900 hover:border-gray-300"
                >
                  <MoreHorizontal className="w-4 h-4" />
                  More
                </button>

                {showMoreTabs && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-10">
                    {hiddenTabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <Link
                          key={tab.id}
                          to={tab.path.replace(':id', projectId.toString())}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setShowMoreTabs(false)}
                        >
                          <Icon className="w-4 h-4" />
                          {tab.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Edit Project Modal */}
        <EditProjectModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSaveProject}
          initialName={currentProject?.name || projectName}
          initialDescription={currentProject?.description || projectDescription}
          isLoading={isUpdating}
        />

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && projectId && (
          <DeleteConfirmDialog
            isOpen={showDeleteConfirm}
            itemName={projectName}
            itemType="project"
            onConfirm={handleDeleteProject}
            onCancel={() => setShowDeleteConfirm(false)}
            isLoading={isDeleting}
            customMessage="Are you sure you want to delete this project? This will hide the project and all its tasks, stories, epics, and issues from the system. This action cannot be undone."
          />
        )}
      </div>
    // </div>
  );
};